// Helpers de export do LineChart — usados no modo avancado pra baixar
// dados crus (CSV) e screenshot do chart (PNG). Sem dependencias
// externas: CSV via string + Blob, PNG via SVG serialization + canvas.

// Trigger universal de download via blob + <a download>. Cobre tanto
// CSV quanto PNG. Revoga a URL apos o click pra liberar memoria.
const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Microtask delay pra garantir que o navegador iniciou o download
  // antes de revogarmos a URL — em alguns browsers (FF antigo) ja era
  // sintoma de "download abortado".
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

// Escapa um campo CSV — se contem virgula/quote/newline, embrulha em
// aspas duplas e duplica as aspas internas (padrao RFC 4180).
const escapeCSVField = (raw) => {
  const s = raw == null ? '' : String(raw);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

// Exporta serie de dados como CSV. data = [{ timestamp, value }].
// timestamp em segundos (Unix epoch); convertemos pra ISO 8601 local
// (mais legivel em Excel/planilhas).
export const exportChartToCSV = ({ data, unit, variableName, filename }) => {
  if (!data || !data.length) return;

  const header = [
    'timestamp',
    'datetime_local',
    `value${unit ? `_${unit}` : ''}`,
  ]
    .map(escapeCSVField)
    .join(',');

  const rows = data.map((point) => {
    const tsMs = Number(point.timestamp) * 1000;
    const iso = Number.isFinite(tsMs) ? new Date(tsMs).toISOString() : '';
    return [point.timestamp, iso, point.value]
      .map(escapeCSVField)
      .join(',');
  });

  // BOM UTF-8 garante que Excel abra com encoding correto (sem BOM ele
  // assume ANSI/Win-1252 e quebra acentos no nome de variavel/header).
  const bom = '﻿';
  const csv = `${bom}${header}\n${rows.join('\n')}\n`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });

  const safeName = (variableName || 'historic').replace(/[^a-zA-Z0-9_-]+/g, '_');
  const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
  triggerDownload(blob, filename || `${safeName}_${ts}.csv`);
};

// Serializa um SVG DOM node + renderiza em canvas + baixa PNG. Recharts
// usa SVG inteiramente entao funciona out-of-the-box. Trick:
// - XMLSerializer transforma o SVG em string
// - blob:image/svg+xml -> <img> carrega
// - canvas.drawImage copia pro raster
// - canvas.toBlob exporta PNG
//
// Limitacoes:
// - <foreignObject> com HTML interno nao renderiza em <img>
//   (security restriction). Recharts nao usa foreignObject por
//   padrao, entao OK.
// - Fontes externas (link CSS) nao "viajam" — usamos serif/sans-serif
//   default. Se quiser fonte custom, embed o @font-face no <defs>.
export const exportChartToPNG = ({ svgNode, filename, scale = 2 }) =>
  new Promise((resolve, reject) => {
    if (!svgNode) {
      reject(new Error('SVG node nao encontrado'));
      return;
    }

    // Clone pra nao mexer no DOM original (pode estar reagindo a
    // hover/tooltip)
    const clone = svgNode.cloneNode(true);

    // Inline width/height — SVG do recharts usa attributes width/
    // height numericos, mas algumas instancias usam viewBox. Garantir
    // dimensoes explicitas evita 0x0 canvas.
    const rect = svgNode.getBoundingClientRect();
    const w = rect.width || 800;
    const h = rect.height || 400;
    clone.setAttribute('width', w);
    clone.setAttribute('height', h);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(clone);
    const svgBlob = new Blob([svgString], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w * scale;
      canvas.height = h * scale;
      const ctx = canvas.getContext('2d');
      // Fundo: o SVG do recharts NAO tem background — usar transparente
      // gera PNG com fundo transparente, util em apresentacoes/reports.
      // Se preferir fundo solido, descomente:
      // ctx.fillStyle = '#ffffff';
      // ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);

      canvas.toBlob((pngBlob) => {
        if (!pngBlob) {
          reject(new Error('canvas.toBlob falhou'));
          return;
        }
        const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        triggerDownload(pngBlob, filename || `chart_${ts}.png`);
        resolve();
      }, 'image/png');
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
